import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, Maximize2, Expand, Minimize2, ArrowLeft, ZoomIn } from 'lucide-react';

function TileDetailModal({ tile, onClose, onEdit, isOpen = true }) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {};
  
  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Auto-play video when modal opens
  useEffect(() => {
    if (tile.mediaType === 'video' && videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch((error) => {
        console.log('Auto-play failed:', error);
      });
    }
  }, [tile.mediaType]);

  // Video control handlers
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoRef.current) {
      if (!isFullscreen) {
        videoRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleVideoPlay = () => setIsPlaying(true);
  const handleVideoPause = () => setIsPlaying(false);

  // Animation variants
  const overlayVariants = {
    hidden: { 
      opacity: 0,
      backdropFilter: 'blur(0px)'
    },
    visible: { 
      opacity: 1,
      backdropFilter: 'blur(16px)',
      transition: {
        duration: 0.3,
        ease: 'easeOut'
      }
    },
    exit: { 
      opacity: 0,
      backdropFilter: 'blur(0px)',
      transition: {
        duration: 0.2,
        ease: 'easeIn'
      }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        mass: 1,
        delayChildren: 0.1,
        staggerChildren: 0.05
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.9,
      y: -30,
      transition: {
        duration: 0.2,
        ease: 'easeIn'
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0,
      y: 20
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25
      }
    }
  };

  const mediaVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.95
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 25,
        delay: 0.1
      }
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleOverlayClick}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div 
            className="relative w-full max-w-4xl max-h-[90vh] flex flex-col gap-4 overflow-auto"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            
            {/* Title Section */}
            <motion.div 
              className="bg-transparent backdrop-blur-md rounded-2xl p-6 border border-gray-700"
              variants={itemVariants}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-white mb-3 leading-tight">
                    {tile.title}
                  </h1>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Created by:</span>
                      <span className="text-white">{tile.createdBy?.username || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Date:</span>
                      <span className="text-white">{new Date(tile.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Type:</span>
                      <span className="text-white capitalize">{tile.mediaType}</span>
                    </div>
                  </div>
                </div>
                <motion.button
                  onClick={onClose}
                  className="p-3 rounded-full bg-transparent backdrop-blur-md hover:bg-gray-800 transition-all text-white border border-gray-700 ml-4"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </div>
            </motion.div>

            {/* Media and Description Container - Side by Side */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Media Section */}
              <motion.div 
                className="flex-1 bg-transparent backdrop-blur-md rounded-2xl overflow-hidden relative border border-gray-700 shadow-2xl"
                style={{ minHeight: '400px', maxHeight: '60vh' }}
                variants={mediaVariants}
              >
                {tile.mediaUrl && (
                  <>
                    {tile.mediaType === 'image' ? (
                      <div className="relative w-full h-full flex items-center justify-center p-6">
                        <motion.img
                          src={tile.mediaUrl}
                          alt={tile.title}
                          className="max-w-full max-h-full object-contain rounded-xl shadow-lg cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageExpanded(true);
                          }}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                          whileHover={{ scale: 1.02 }}
                        />
                        {/* Expand button overlay */}
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageExpanded(true);
                          }}
                          className="absolute top-8 right-8 p-3 rounded-full bg-black bg-opacity-50 backdrop-blur-md hover:bg-opacity-70 transition-all text-white"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Expand image"
                        >
                          <ZoomIn className="w-5 h-5" />
                        </motion.button>
                      </div>
                    ) : tile.mediaType === 'video' ? (
                      <div className="relative w-full h-full flex items-center justify-center p-6">
                        <motion.video
                          ref={videoRef}
                          className="max-w-full max-h-full object-contain rounded-xl shadow-lg"
                          onPlay={handleVideoPlay}
                          onPause={handleVideoPause}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlayPause();
                          }}
                          loop
                          muted={isMuted}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2, duration: 0.3 }}
                        >
                          <source src={tile.mediaUrl} type="video/mp4" />
                          <source src={tile.mediaUrl} type="video/webm" />
                          Your browser does not support the video tag.
                        </motion.video>
                        
                        {/* Video Controls Overlay */}
                        <motion.div 
                          className="absolute bottom-8 left-8 right-8 bg-black bg-opacity-70 backdrop-blur-md rounded-2xl p-4 opacity-0 hover:opacity-100 transition-opacity duration-300"
                          initial={{ y: 20, opacity: 0 }}
                          animate={{ y: 0, opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePlayPause();
                                }}
                                className="p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                              </motion.button>
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMute();
                                }}
                                className="p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                              </motion.button>
                            </div>
                            <motion.button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFullscreen();
                              }}
                              className="p-3 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all text-white"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Maximize2 className="w-5 h-5" />
                            </motion.button>
                          </div>
                        </motion.div>

                        {/* Play/Pause Overlay */}
                        <AnimatePresence>
                          {!isPlaying && (
                            <motion.div 
                              className="absolute inset-0 flex items-center justify-center pointer-events-none"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.2 }}
                            >
                              <div className="p-6 rounded-full bg-black bg-opacity-60 backdrop-blur-sm">
                                <Play className="w-16 h-16 text-white ml-1" />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : null}
                  </>
                )}
              </motion.div>

              {/* Description Section */}
              {tile.description && (
                <AnimatePresence mode="wait">
                  {isDescriptionExpanded ? (
                    // Expanded view
                    <motion.div
                      key="expanded"
                      className="fixed inset-0 bg-black bg-opacity-90 z-60 flex items-center justify-center p-8"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setIsDescriptionExpanded(false)}
                    >
                      <motion.div
                        className="bg-transparent backdrop-blur-md rounded-2xl p-8 border border-gray-700 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between mb-6">
                          <h2 className="text-2xl font-semibold text-white">
                            Description
                          </h2>
                          <button
                            onClick={() => setIsDescriptionExpanded(false)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all border border-gray-600"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                          </button>
                        </div>
                        <div className="text-gray-300 leading-relaxed whitespace-pre-wrap overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 text-lg">
                          {tile.description}
                        </div>
                      </motion.div>
                    </motion.div>
                  ) : (
                    // Normal view
                    <motion.div 
                      key="normal"
                      className="flex-1 bg-transparent backdrop-blur-md rounded-2xl p-6 border border-gray-700 relative flex flex-col"
                      style={{ minHeight: '400px', maxHeight: '60vh' }}
                      variants={itemVariants}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-white">
                          Description
                        </h2>
                        <motion.button
                          onClick={() => setIsDescriptionExpanded(true)}
                          className="p-2 rounded-full bg-white bg-opacity-10 hover:bg-opacity-20 transition-all text-white"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title="Expand description"
                        >
                          <Expand className="w-4 h-4" />
                        </motion.button>
                      </div>
                      <div className="text-gray-300 leading-relaxed whitespace-pre-wrap overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
                        {tile.description}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>

            {/* Expanded Image Modal */}
            <AnimatePresence>
              {isImageExpanded && tile.mediaType === 'image' && (
                <motion.div
                  className="fixed inset-0 bg-black bg-opacity-95 z-[70] flex items-center justify-center p-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setIsImageExpanded(false)}
                >
                  <motion.div
                    className="relative w-full h-full flex items-center justify-center"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={tile.mediaUrl}
                      alt={tile.title}
                      className="max-w-[90vw] max-h-[85vh] object-contain"
                      style={{ width: 'auto', height: 'auto' }}
                    />
                    <motion.button
                      onClick={() => setIsImageExpanded(false)}
                      className="absolute top-4 right-4 p-3 rounded-full bg-black bg-opacity-70 backdrop-blur-md hover:bg-opacity-80 transition-all text-white"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <X className="w-6 h-6" />
                    </motion.button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Action Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-3"
              variants={itemVariants}
            >
              <motion.button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all border border-gray-600 font-medium"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Close
              </motion.button>
              {onEdit && user.isAdmin && (tile.createdBy?.username === user.username || tile.createdBy?._id === user.id) && (
                <motion.button
                  onClick={onEdit}
                  className="flex-1 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-xl transition-all font-medium shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Edit
                </motion.button>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TileDetailModal;